import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';

// Admin billing — start a Stripe Checkout subscription or open the Customer Portal.
// Subscription state is written to the vendor doc by the stripeWebhook function.

const PRETTY = { basic: 'Basic', enterprise: 'Enterprise', free: 'Free', pilot: 'Pilot' };
const SUB_ACTIVE = ['active', 'trialing', 'past_due'];

export default function Billing() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [vendor, setVendor] = useState(null);
  const [tier, setTier] = useState('basic');
  const [billing, setBilling] = useState('annual');
  const [aiAddon, setAiAddon] = useState(false);
  const [dedicatedNumber, setDedicatedNumber] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [seatCount, setSeatCount] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(doc(db, 'vendors', vendorId), (s) => setVendor(s.data() || {}));
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(
      query(collection(db, 'vendors', vendorId, 'users'), where('role', 'in', ['responder', 'vendor_admin'])),
      (snap) => setSeatCount(snap.docs.filter((d) => !d.data().deletedAt).length),
    );
  }, [vendorId]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('billing');
    if (p === 'success') setNotice('Subscription started — thank you. It can take a few seconds to show below.');
    if (p === 'cancel') setNotice('Checkout canceled — no changes were made.');
  }, []);

  const status = vendor?.subscriptionStatus;
  const hasSubscription = !!vendor?.stripeSubscriptionId && SUB_ACTIVE.includes(status);
  const currentPlan = PRETTY[vendor?.planTier] || vendor?.planTier || '—';

  async function subscribe() {
    setBusy(true); setError(null);
    try {
      const res = await httpsCallable(functions, 'createCheckoutSession')({ tier, billing, aiAddon, dedicatedNumber });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.message || 'Could not start checkout.');
      setBusy(false);
    }
  }

  async function manage() {
    setBusy(true); setError(null);
    try {
      const res = await httpsCallable(functions, 'createBillingPortalSession')({});
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.message || 'Could not open the billing portal.');
      setBusy(false);
    }
  }

  return (
    <>
      <h2 style={{ margin: 0 }}>Billing</h2>
      <p style={{ color: 'var(--muted)', marginTop: 4 }}>
        Per-seat subscription (responders + administrators). Requesters are always free.
        Billed securely by Stripe.
      </p>

      {notice && (
        <div style={{ ...box, background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46' }}>{notice}</div>
      )}
      {error && <div className="error">{error}</div>}

      <div style={box}>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>Current plan</div>
        <div style={{ fontSize: 24, fontWeight: 700, margin: '2px 0' }}>{currentPlan}</div>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>
          {hasSubscription
            ? `Subscription ${status}${vendor?.aiAddon?.enabled ? ' · AI add-on on' : ''}`
            : 'No active paid subscription.'}
        </div>
      </div>

      {hasSubscription ? (
        <button onClick={manage} disabled={busy} className="btn-primary">
          {busy ? 'Opening…' : 'Manage billing'}
        </button>
      ) : (
        <div style={box}>
          <h3 style={{ marginTop: 0 }}>Subscribe</h3>

          <label style={lbl}>Plan</label>
          <select value={tier} onChange={(e) => setTier(e.target.value)} className="settings-select" style={{ maxWidth: 260 }}>
            <option value="basic">Basic — $16/seat/mo</option>
            <option value="enterprise">Enterprise — $29/seat/mo</option>
          </select>

          <label style={lbl}>Billing period</label>
          <select value={billing} onChange={(e) => setBilling(e.target.value)} className="settings-select" style={{ maxWidth: 260 }}>
            <option value="annual">Annual — 2 months free</option>
            <option value="monthly">Monthly</option>
          </select>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 14 }}>
            <input type="checkbox" checked={aiAddon} onChange={(e) => setAiAddon(e.target.checked)} />
            Add the AI add-on (+$15/month)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 14 }}>
            <input type="checkbox" checked={dedicatedNumber} onChange={(e) => setDedicatedNumber(e.target.checked)} />
            Dedicated phone number (+$10/month) <span style={{ color: 'var(--muted)', fontSize: 12 }}>— provisioning handled separately</span>
          </label>

          <p style={{ fontSize: 13, marginTop: 12 }}>
            You'll be billed for your current seat count:{' '}
            <strong>{seatCount == null ? '…' : `${seatCount} seat${seatCount === 1 ? '' : 's'}`}</strong>{' '}
            <span style={{ color: 'var(--muted)' }}>(responders + administrators; requesters are free)</span>.
          </p>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
            Add a seat later and we charge the pro-rata difference; remove one and the lower
            count applies at your next renewal (no mid-term credit). Stripe opens in this tab;
            change or cancel anytime from “Manage billing”.
          </p>

          <button onClick={subscribe} disabled={busy} className="btn-primary" style={{ marginTop: 8 }}>
            {busy ? 'Starting checkout…' : 'Continue to checkout'}
          </button>
        </div>
      )}
    </>
  );
}

const box = {
  border: '1px solid var(--border, #e2e8f0)',
  borderRadius: 'var(--radius, 10px)',
  padding: '14px 16px',
  marginTop: 14,
  background: 'var(--card, #fff)',
};
const lbl = { display: 'block', marginTop: 12, marginBottom: 4, fontSize: 13, fontWeight: 600 };
